import { AccessorFnColumnDef, ColumnHelper, Row } from "@tanstack/react-table";
import isNil from "lodash/isNil";
import { ManageRolesTableDataItem } from "PFApp/activities/manage_roles/manage_roles.types";
import { useInteractiveFormHandlers } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/hooks/use_interactive_form_handlers";
import { useRolesTableColumns } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/hooks/use_roles_table_columns";
import css from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/tabular_roles_form.module.scss";
import { getRoleFormKeyPrefix } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/tabular_roles_form.utils";
import {
  FIELD_AUDIENCES_KEY,
  FIELD_AVAILABILITY_KEY,
  FIELD_CONVERTED_AVAILABILITY_KEY,
  FIELD_MARKED_FOR_REMOVAL_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { useMultiActivityFormStore } from "PFApp/stores/activity_form/multi_roles_form";
import { useManageRolesStore } from "PFApp/stores/manage_roles";
import { RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import Table, { DragAxis, InteractiveTable } from "PFComponents/table";
import { Subtemplate } from "PFCore/types";
import { useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";

import { useFormUnsavedEditsTracker } from "../hooks/use_form_unsaved_edits_tracker";
import { useManageRolesColumns } from "../hooks/use_manage_roles_columns";
import {
  getManageRolesColumnsOrder,
  getRoleKey,
  getSubtemplateIndex,
  getTrackedKeysFromSchemaFields,
  mergeManageRolesColumns
} from "../manage_roles.utils";
import { wrapColumnsWithHighlight } from "../manage_roles_cell_highlight";
import { useUnsavedEditsSyncContext } from "./unsaved_edits_sync_context";

const READONLY_COLUMN_NAMES = ["original_wm", "booked_wm"];

type ManageRolesFormProps = {
  subtemplate: Subtemplate | null;
  rolesFormKey: RolesFormKey;
  getRowClassName?: (row: Row<ManageRolesTableDataItem>, rowIndex: number) => string | undefined;
};

export const ManageRolesForm = ({ subtemplate, rolesFormKey, getRowClassName }: ManageRolesFormProps) => {
  const scrollableContainerRef = useMultiActivityFormStore((store) => store.scrollableContainerRef);
  const currentPage = useManageRolesStore((state) => state.currentPage);
  const selectedColumnNames = useManageRolesStore((state) => state.selectedColumns);
  const roleTemplate = useTemplate({ key: TemplateKey.Role });
  const engagementTemplate = useTemplate({ key: TemplateKey.Engagement });
  const isParentActivityVisible = roleTemplate?.schema?.rules?.hide_parent_activity_id !== true;
  const isEngagementVisible = isParentActivityVisible && !!engagementTemplate;

  const tableRowsInfo = useWatch<ManageRolesMultiForm, RolesFormKey, unknown, ManageRolesTableDataItem[]>({
    name: rolesFormKey,
    compute: (roles = []) =>
      (roles as (ManageRolesFormType & { markedForRemoval?: boolean })[]).map((role) => ({
        uniqueId: role.uniqueId,
        disabled: !!role[FIELD_MARKED_FOR_REMOVAL_KEY],
        engagement:
          !isNil(role.engagement_id) && !isNil(role[FIELD_PARENT_ACTIVITY_KEY])
            ? {
                id: role[FIELD_PARENT_ACTIVITY_KEY]?.id as number,
                name: role[FIELD_PARENT_ACTIVITY_KEY]?.name as string
              }
            : undefined,
        activityId: role.id,
        roleKey: getRoleKey(role)
      }))
  });

  const rolesCount = tableRowsInfo.length;

  const tableInstanceRef = useRef(null);

  const {
    generateColumns: generateSchemaColumns,
    hiddenFormControllers,
    formSchemaFields,
    isFieldRequired,
    getFieldPath,
    getFieldByColumnId,
    getCellBehavior
  } = useRolesTableColumns({
    subtemplate,
    rolesFormKey,
    rolesCount,
    tableInstanceRef,
    isReadonly: false,
    isEngagementVisible,
    isAudit: false,
    isManageRolesView: true
  });

  const { handleFill, handlePaste, handleClear } = useInteractiveFormHandlers({
    tableInstanceRef,
    rolesFormKey,
    tableRowsInfo,
    getFieldPath,
    getFieldByColumnId,
    getCellBehavior
  });

  const trackedKeys = useMemo(() => {
    const schemaKeys = getTrackedKeysFromSchemaFields(formSchemaFields);
    schemaKeys.add(FIELD_AVAILABILITY_KEY);
    schemaKeys.add(FIELD_CONVERTED_AVAILABILITY_KEY);
    schemaKeys.add(FIELD_PARENT_ACTIVITY_KEY);
    schemaKeys.add(FIELD_POST_UNTIL_CHANGE_REASON_KEY);
    schemaKeys.add(FIELD_AUDIENCES_KEY);
    return schemaKeys;
  }, [formSchemaFields]);

  const requiredKeys = useMemo(
    () =>
      new Set(
        formSchemaFields.filter(isFieldRequired).map((field) => `${getRoleFormKeyPrefix(field)}${field.name}`)
      ),
    [formSchemaFields, isFieldRequired]
  );

  const subtemplateIndex = getSubtemplateIndex(rolesFormKey);

  const { registerRequiredKeys, unregisterRequiredKeys, registerTrackedKeys, unregisterTrackedKeys } =
    useUnsavedEditsSyncContext();

  useEffect(() => {
    registerRequiredKeys(subtemplateIndex, requiredKeys);
    return () => {
      unregisterRequiredKeys(subtemplateIndex);
    };
  }, [registerRequiredKeys, unregisterRequiredKeys, subtemplateIndex, requiredKeys]);

  useEffect(() => {
    registerTrackedKeys(subtemplateIndex, trackedKeys);
    return () => {
      unregisterTrackedKeys(subtemplateIndex);
    };
  }, [registerTrackedKeys, unregisterTrackedKeys, subtemplateIndex, trackedKeys]);

  const columnIdToField = new Map(formSchemaFields.map((field) => [field.name, field]));

  useFormUnsavedEditsTracker({ rolesFormKey, trackedKeys, currentPage });

  const { generateColumns: generateCustomColumns } = useManageRolesColumns({ rolesFormKey });

  const getRoleId = (rowIndex: number) => (tableRowsInfo[rowIndex] as ManageRolesTableDataItem)?.roleKey;
  const getField = (col: AccessorFnColumnDef<ManageRolesTableDataItem, any>) =>
    columnIdToField.get(col.id as string) ?? { name: col.id as string };

  const getIsReadonlyColumn = (col: AccessorFnColumnDef<ManageRolesTableDataItem, any>) =>
    selectedColumnNames.includes(col.id as string) || READONLY_COLUMN_NAMES.includes(col.id as string);

  const columnsOrder = useMemo(() => getManageRolesColumnsOrder(selectedColumnNames), [selectedColumnNames]);

  const generateMergedColumns = (columnHelper: ColumnHelper<ManageRolesTableDataItem>) => {
    const schemaColumns = generateSchemaColumns(columnHelper);
    const customColumns = generateCustomColumns(columnHelper);

    const wrappedSchemaColumns = wrapColumnsWithHighlight(
      schemaColumns,
      getRoleId,
      getField,
      getIsReadonlyColumn
    );
    const wrappedCustomColumns = wrapColumnsWithHighlight(
      customColumns,
      getRoleId,
      getField,
      getIsReadonlyColumn
    );

    return mergeManageRolesColumns(wrappedSchemaColumns, wrappedCustomColumns, columnsOrder);
  };

  return (
    <>
      {hiddenFormControllers}
      <InteractiveTable
        dragAxis={DragAxis.Vertical}
        tableInstanceRef={tableInstanceRef}
        restrictPasteToSourceColumn
        onFill={handleFill}
        onPaste={handlePaste}
        onClear={handleClear}
        scrollableContainerRef={scrollableContainerRef}
      >
        <Table
          generateColumns={generateMergedColumns}
          tableData={tableRowsInfo}
          className={css.table}
          getRowClassName={getRowClassName}
          getRowId={(row) => row.uniqueId}
          enableSorting={false}
          tableInstanceRef={tableInstanceRef}
          scrollableContainerRef={scrollableContainerRef}
          hideDistantRows
          noHover
          displayGrid
          disableInternalScrolling
          disableAutoResizing
          isInteractive
        />
      </InteractiveTable>
    </>
  );
};
